import { useEffect, useRef } from 'react';

import { isEconomicResearchRunActive } from '../../../services/economicResearchClient';
import type { EconomicResearchRunView } from '../../../types/economicResearch';

export interface EconomicResearchImprovementNotice {
  runId: string;
  title: string;
  description: string;
}

export type EconomicResearchImprovementNotifier = (
  notice: EconomicResearchImprovementNotice,
) => void;

/**
 * Return a notice only when a completed run changed registry evidence and
 * produced at least one policy-valid match for the current site. A same-hash
 * or semantically idempotent re-promotion deliberately stays quiet.
 */
export function economicResearchImprovementNotice(
  run: EconomicResearchRunView,
): EconomicResearchImprovementNotice | null {
  const changed = run.counts.recordsChanged ?? 0;
  const matched = run.resultSnapshot?.policyValidRecords.length ?? 0;
  if (
    run.stage !== 'complete'
    || run.terminalResult !== 'complete-with-evidence'
    || changed < 1
    || matched < 1
  ) return null;

  return {
    runId: run.runId,
    title: 'Comparable-project answer updated',
    description: changed === 1
      ? '1 policy-valid public record was added or enriched in Findings.'
      : `${changed} policy-valid public records were added or enriched in Findings.`,
  };
}

/**
 * Notify once for an in-view background transition from active to improved.
 * Initial terminal hydration deliberately stays quiet, so reopening a site
 * never repeats an old notification or pulls attention from the current task.
 */
export function useEconomicResearchImprovementNotification(
  run: EconomicResearchRunView | null,
  notify: EconomicResearchImprovementNotifier,
): void {
  const previousRunRef = useRef<EconomicResearchRunView | null>(null);
  const notifiedRunIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const previous = previousRunRef.current;
    previousRunRef.current = run;
    if (
      !previous
      || !run
      || previous.runId !== run.runId
      || !isEconomicResearchRunActive(previous)
      || notifiedRunIdsRef.current.has(run.runId)
    ) return;

    const notice = economicResearchImprovementNotice(run);
    if (!notice) return;
    notifiedRunIdsRef.current.add(run.runId);
    try {
      notify(notice);
    } catch {
      // A missing toast host must never interrupt Findings or research polling.
    }
  }, [notify, run]);
}
